// Actions — board section 02: the things a user presses, and the status
// affordances that sit beside them. Every variant/size/shape literal of
// Button, IconButton, Chip and Progress renders here.

import { Button, Chip, Icon, IconButton, Progress } from '@/components/common';
import Spec from './Spec';

const ActionsSection = () => (
  <section className='theme-page__section'>
    <h2 className='theme-page__section-title'>
      <Icon name='mouse-pointer-click' size={18} /> 02 · Actions
    </h2>

    {/* ---- Button ---- */}
    <h3 className='theme-page__block-title'>
      Button · variant
      <span className='theme-page__note'>One primary per view — everything else steps down.</span>
    </h3>
    <div className='theme-page__variants'>
      <Spec label="variant='primary'">
        <Button iconStart='plus'>Add task</Button>
      </Spec>
      <Spec label="variant='secondary'">
        <Button variant='secondary'>Cancel</Button>
      </Spec>
      <Spec label="variant='ghost'">
        <Button variant='ghost' iconStart='ellipsis'>
          More
        </Button>
      </Spec>
      <Spec label="variant='danger'">
        <Button variant='danger' iconStart='trash-2'>
          Delete
        </Button>
      </Spec>
      <Spec label="variant='danger-outline'">
        <Button variant='danger-outline' iconStart='trash-2'>
          Delete company
        </Button>
      </Spec>
    </div>

    <h3 className='theme-page__block-title'>Button · size</h3>
    <div className='theme-page__variants'>
      <Spec label="size='lg'">
        <Button size='lg'>Large action</Button>
      </Spec>
      <Spec label="size='md' (default)">
        <Button size='md'>Default</Button>
      </Spec>
      <Spec label="size='sm'">
        <Button size='sm'>Small</Button>
      </Spec>
    </div>

    <h3 className='theme-page__block-title'>Button · shape</h3>
    <div className='theme-page__variants'>
      <Spec label="shape='rounded' (default)">
        <Button iconStart='send'>Send to team</Button>
      </Spec>
      <Spec label="shape='pill'">
        <Button shape='pill' iconStart='send'>
          Send to team
        </Button>
      </Spec>
      <Spec label="shape='pill' · secondary">
        <Button variant='secondary' shape='pill'>
          Invite people
        </Button>
      </Spec>
    </div>

    <h3 className='theme-page__block-title'>
      Button · icons &amp; states
      <span className='theme-page__note'>
        `loading` swaps both icons for the spinner and disables.
      </span>
    </h3>
    <div className='theme-page__variants'>
      <Spec label='iconStart'>
        <Button iconStart='save'>Save</Button>
      </Spec>
      <Spec label='iconEnd'>
        <Button variant='secondary' iconEnd='arrow-right'>
          Continue
        </Button>
      </Spec>
      <Spec label='iconStart + iconEnd'>
        <Button variant='secondary' iconStart='download' iconEnd='chevron-down'>
          Export
        </Button>
      </Spec>
      <Spec label='loading'>
        <Button loading>Saving</Button>
      </Spec>
      <Spec label='disabled'>
        <Button disabled>Disabled</Button>
      </Spec>
      <Spec label='disabled · secondary'>
        <Button variant='secondary' disabled>
          Disabled
        </Button>
      </Spec>
    </div>

    {/* ---- IconButton ---- */}
    <h3 className='theme-page__block-title'>
      IconButton · variant &amp; size
      <span className='theme-page__note'>`aria-label` is required — there is no visible text.</span>
    </h3>
    <div className='theme-page__variants'>
      <Spec label="variant='outline' (default)">
        <IconButton icon='pencil' aria-label='Edit' />
      </Spec>
      <Spec label="variant='ghost'">
        <IconButton icon='x' variant='ghost' aria-label='Close' />
      </Spec>
      <Spec label="size='lg'">
        <IconButton icon='sliders-horizontal' size='lg' aria-label='Filters' />
      </Spec>
      <Spec label="size='md' (default)">
        <IconButton icon='sliders-horizontal' size='md' aria-label='Filters' />
      </Spec>
      <Spec label="size='sm'">
        <IconButton icon='sliders-horizontal' size='sm' aria-label='Filters' />
      </Spec>
      <Spec label='disabled'>
        <IconButton icon='ellipsis-vertical' aria-label='More' disabled />
      </Spec>
    </div>

    {/* ---- Chip ---- */}
    <h3 className='theme-page__block-title'>
      Chip · variant
      <span className='theme-page__note'>Status is colour + text — never colour alone.</span>
    </h3>
    <div className='theme-page__variants'>
      <Spec label="variant='brand'">
        <Chip variant='brand'>Brand</Chip>
      </Spec>
      <Spec label="variant='brand-solid'">
        <Chip variant='brand-solid' iconStart='check'>
          Brand solid
        </Chip>
      </Spec>
      <Spec label="variant='success'">
        <Chip variant='success' dot>
          Active
        </Chip>
      </Spec>
      <Spec label="variant='info'">
        <Chip variant='info' dot>
          In review
        </Chip>
      </Spec>
      <Spec label="variant='warning'">
        <Chip variant='warning' dot>
          Pending
        </Chip>
      </Spec>
      <Spec label="variant='danger'">
        <Chip variant='danger' dot>
          Overdue
        </Chip>
      </Spec>
      <Spec label="variant='purple'">
        <Chip variant='purple'>Beta</Chip>
      </Spec>
      <Spec label="variant='neutral' (default)">
        <Chip variant='neutral' iconStart='clock'>
          Draft
        </Chip>
      </Spec>
      <Spec label="variant='outline'">
        <Chip variant='outline'>Outline</Chip>
      </Spec>
    </div>

    <h3 className='theme-page__block-title'>Chip · size &amp; affordances</h3>
    <div className='theme-page__variants'>
      <Spec label="size='md' (default)">
        <Chip variant='brand'>Medium</Chip>
      </Spec>
      <Spec label="size='lg'">
        <Chip variant='brand' size='lg'>
          Large
        </Chip>
      </Spec>
      <Spec label='dot'>
        <Chip variant='success' dot>
          With dot
        </Chip>
      </Spec>
      <Spec label='iconStart'>
        <Chip variant='info' iconStart='clock'>
          With icon
        </Chip>
      </Spec>
      <Spec label="size='lg' + iconStart">
        <Chip variant='warning' size='lg' iconStart='triangle-alert'>
          Large + icon
        </Chip>
      </Spec>
    </div>

    {/* ---- Progress ---- */}
    <h3 className='theme-page__block-title'>
      Progress · variant
      <span className='theme-page__note'>Values clamp to 0–100% of `max`.</span>
    </h3>
    <div className='theme-page__col'>
      <div className='theme-page__meter-row'>
        <span className='theme-page__token-name'>variant=&apos;brand&apos; (default)</span>
        <Progress value={72} />
        <span className='theme-page__token-value'>72</span>
      </div>
      <div className='theme-page__meter-row'>
        <span className='theme-page__token-name'>variant=&apos;success&apos;</span>
        <Progress value={40} variant='success' />
        <span className='theme-page__token-value'>40</span>
      </div>
      <div className='theme-page__meter-row'>
        <span className='theme-page__token-name'>variant=&apos;warning&apos;</span>
        <Progress value={85} variant='warning' />
        <span className='theme-page__token-value'>85</span>
      </div>
      <div className='theme-page__meter-row'>
        <span className='theme-page__token-name'>variant=&apos;danger&apos;</span>
        <Progress value={110} max={100} variant='danger' />
        <span className='theme-page__token-value'>110 → clamped</span>
      </div>
      <div className='theme-page__meter-row'>
        <span className='theme-page__token-name'>max=&apos;200&apos;</span>
        <Progress value={50} max={200} />
        <span className='theme-page__token-value'>50 / 200</span>
      </div>
    </div>
  </section>
);

export default ActionsSection;
